import React from 'react';
import { ACCUSATION_OUTCOMES, DETENTION_TYPES, DUE_PROCESS_VIOLATIONS } from '../types/caseForm';
import { describeValue } from '../types/taxonomy';

// "Who is charged or detained, and what happened to them" in one place. Rows come from each
// victim's CRIMINALIZATION (and ARBITRARY_DETENTION) tag; the same facts are repeated on the
// victim cards, so each row links back to its victim.

export interface OutcomeTableRow {
  victimId: string;
  label: string;            // display name (already masked by the page when required)
  isGroup: boolean;
  affectedCount?: number | null;
  violation: 'Criminalization' | 'Arbitrary detention';
  accusationOutcome?: string | null;
  detentionType?: string | null;
  detentionDays?: number | null;
  dueProcessViolations?: string[];
}

interface Props {
  rows: OutcomeTableRow[];
  onJump: (victimId: string) => void;
}

const optionLabel = (options: { value: string; label: string }[], value: string) =>
  options.find((o) => o.value === value)?.label || value;
const dash = <span className="text-gray-300 dark:text-gray-600">—</span>;

// Only a conviction is coloured; other outcomes read as neutral facts.
const OUTCOME_PILL: Record<string, string> = {
  CONVICTED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
const NEUTRAL = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

const days = (n: number) => (n === 0 ? 'less than a day' : `${n} ${n === 1 ? 'day' : 'days'}`);

const VictimOutcomesTable: React.FC<Props> = ({ rows, onJump }) => {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr className="text-left text-gray-500 dark:text-gray-400">
            <th className="px-3 py-2 font-medium">Victim</th>
            <th className="px-3 py-2 font-medium">Outcome</th>
            <th className="px-3 py-2 font-medium">Detention</th>
            <th className="px-3 py-2 font-medium">Due-process violations</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((r) => {
            const dpv = r.dueProcessViolations || [];
            return (
              <tr key={`${r.victimId}-${r.violation}`}>
                <td className="px-3 py-2 align-top">
                  <button
                    type="button"
                    onClick={() => onJump(r.victimId)}
                    title="Show this victim"
                    className="text-left font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 hover:underline"
                  >
                    {r.label}
                  </button>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {r.violation}{r.isGroup && r.affectedCount != null ? ` · ${r.affectedCount} people` : ''}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  {r.accusationOutcome ? (
                    <span title={describeValue('accusationOutcome', r.accusationOutcome)} className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${OUTCOME_PILL[r.accusationOutcome] || NEUTRAL}`}>{optionLabel(ACCUSATION_OUTCOMES, r.accusationOutcome)}</span>
                  ) : dash}
                </td>
                <td className="px-3 py-2 align-top text-gray-700 dark:text-gray-300">
                  {r.detentionType || r.detentionDays != null ? (
                    <>
                      {r.detentionType ? <span title={describeValue('detentionType', r.detentionType)}>{optionLabel(DETENTION_TYPES, r.detentionType)}</span> : ''}
                      {r.detentionType && r.detentionDays != null ? ' · ' : ''}
                      {r.detentionDays != null ? days(r.detentionDays) : ''}
                    </>
                  ) : dash}
                </td>
                <td className="px-3 py-2 align-top">
                  {dpv.length ? (
                    <span className="inline-flex flex-wrap gap-1">
                      {dpv.map((v) => <span key={v} title={describeValue('dueProcessViolations', v)} className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">{optionLabel(DUE_PROCESS_VIOLATIONS, v)}</span>)}
                    </span>
                  ) : dash}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default VictimOutcomesTable;
